// Core configuration types
export interface VijiCoreConfig {
  /** Container element where the scene will be rendered */
  hostContainer: HTMLElement;
  
  /** Artist scene code to execute */
  sceneCode: string;
  
  /** Frame rate mode - 'full' for every animation frame, 'half' for every second frame */
  frameRateMode?: FrameRateMode;
  
  /** Enable automatic performance optimization */
  autoOptimize?: boolean;
  
  /** Audio input stream */
  audioStream?: MediaStream;
  
  /** Video input stream */
  videoStream?: MediaStream;
  
  /** Audio analysis configuration */
  analysisConfig?: AnalysisConfiguration;
  
  /** Initial parameter values */
  parameters?: ParameterGroup[];
  
  /** Disable input processing (for preview instances) */
  noInputs?: boolean;
  
  /** Enable user interaction events */
  allowUserInteraction?: boolean;
}

// Capture frame options
export interface CaptureFrameOptions {
  /** MIME type for output, e.g., 'image/png', 'image/jpeg', 'image/webp' */
  type?: string;
  /**
   * Target resolution.
   * - number: scale factor relative to current canvas size (e.g., 0.5 = 50%)
   * - { width, height }: exact output size; if aspect ratio differs from canvas,
   *   the source is center-cropped to match the target aspect ratio before scaling
   */
  resolution?: number | { width: number; height: number };
}

// Core capabilities state
export interface CoreCapabilities {
  /** Audio input is active and connected */
  hasAudio: boolean;
  
  /** Video input is active and connected */
  hasVideo: boolean;
  
  /** User interactions (mouse, touch, keyboard) are enabled */
  hasInteraction: boolean;
  
  /** General parameters are always available */
  hasGeneral: boolean;
}

// Resolution types
export type Resolution = { width: number; height: number; }

// Parameter category system
export type ParameterCategory = 'audio' | 'video' | 'interaction' | 'general';

// Enhanced parameter system types for Phase 2
export interface ParameterGroup {
  groupName: string;
  category?: ParameterCategory;
  description?: string;
  collapsed?: boolean;
  order?: number;
  parameters: Record<string, ParameterDefinition>;
}

export interface ParameterDefinition {
  type: 'slider' | 'color' | 'toggle' | 'select' | 'text' | 'number' | 'image';
  defaultValue: any;
  label?: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
  config?: ParameterConfig;
  // Validation function
  validate?: (value: any) => boolean;
}

export interface ParameterConfig {
  // For slider/number types
  min?: number;
  max?: number;
  step?: number;
  
  // For select type
  options?: string[] | number[];
  
  // For text type
  maxLength?: number;
  
  // UI configuration
  label?: string;
  description?: string;
  hidden?: boolean;
  
  // Advanced configuration
  precision?: number; // For number display precision
}

// Parameter object types (returned by helper functions)
export interface SliderParameter {
  value: number;
  min: number;
  max: number;
  step: number;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface ColorParameter {
  value: string;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface ToggleParameter {
  value: boolean;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface SelectParameter {
  value: string | number;
  options: string[] | number[];
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface TextParameter {
  value: string;
  maxLength?: number;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface NumberParameter {
  value: number;
  min: number;
  max: number;
  step: number;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

export interface ImageParameter {
  value: ImageBitmap | OffscreenCanvas | null;
  label: string;
  description?: string;
  group: string;
  category: ParameterCategory;
}

// Parameter helper function configurations
export interface SliderConfig {
  min?: number;
  max?: number;
  step?: number;
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface ColorConfig {
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface ToggleConfig {
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface SelectConfig {
  options: string[] | number[];
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface TextConfig {
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
  maxLength?: number;
}

export interface NumberConfig {
  min?: number;
  max?: number;
  step?: number;
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

export interface ImageConfig {
  label: string;
  description?: string;
  group?: string;
  category?: ParameterCategory;
}

// Parameter value types
export type ParameterValue = string | number | boolean | ImageBitmap | OffscreenCanvas | null;

// Parameter update message data
export interface ParameterUpdateData {
  name: string;
  value: ParameterValue;
}

export interface ParameterBatchUpdateData {
  updates: ParameterUpdateData[];
}

// Parameter definition extraction data
export interface ParameterDefinitionData {
  groups: ParameterGroup[];
}

// Audio analysis configuration
export interface AnalysisConfiguration {
  fftSize?: number;
  smoothing?: number;
  frequencyBands?: FrequencyBand[];
  beatDetection?: boolean;
  onsetDetection?: boolean;
}

export interface FrequencyBand {
  name: string;
  min: number;
  max: number;
}

// Frame rate mode types
export type FrameRateMode = 'full' | 'half';

// CV frame rate mode types (relative to scene processing rate)
export type CVFrameRateMode = 'full' | 'half' | 'quarter' | 'eighth';

// Renderer type for P5.js support
export type RendererType = 'native' | 'p5' | 'shader';

export interface FrameRateInfo {
  mode: FrameRateMode;
  screenRefreshRate: number;
  effectiveRefreshRate: number;
}

// Performance monitoring
export interface PerformanceStats {
  frameTime: number;
  resolution: Resolution;
  scale: number;
  frameRate: FrameRateInfo;
  rendererType: RendererType;
  memoryUsage?: number;
  activeStreams?: number;
  parameterCount?: number;
  // CV Processing Stats (integrated)
  cv?: {
    activeFeatures: CVFeature[];
    processingTime: number; // average processing time in milliseconds
    targetFPS: number; // target CV FPS based on scene rate and CV rate mode
    actualFPS: number; // actual measured CV processing FPS
    isProcessing: boolean;
  };
}

// Communication message types
export interface Message {
  type: string;
  id: string;
  timestamp: number;
  data?: any;
}

export interface HostToWorkerMessage extends Message {
  type:
    | 'init'
    | 'parameter-update'
    | 'parameter-batch-update'
    | 'stream-update'
    | 'performance-update'
    | 'capture-frame';
}

export interface WorkerToHostMessage extends Message {
  type:
    | 'init-response'
    | 'ready'
    | 'parameters-defined'
    | 'parameter-validation-error'
    | 'error'
    | 'performance-warning'
    | 'capture-frame-result';
}

// Resource management types
export interface ResourceTracker {
  workers: Set<Worker>;
  iframes: Set<HTMLIFrameElement>;
  streams: Set<MediaStream>;
  contexts: Set<AudioContext | CanvasRenderingContext2D | WebGLRenderingContext>;
}

// Enhanced Artist API types (provided to artist code)
export interface VijiAPI {
  // Canvas and rendering
  canvas: OffscreenCanvas;
  ctx?: OffscreenCanvasRenderingContext2D;
  gl?: WebGL2RenderingContext;
  width: number;
  height: number;
  pixelRatio: number;
  
  // Timing
  time: number;
  deltaTime: number;
  frameCount: number;
  fps: number;
  
  // Audio (when available)
  audio: AudioAPI;
  
  // Video (when available)  
  video: VideoAPI;
  
  // User interaction
  mouse: MouseAPI;
  keyboard: KeyboardAPI;
  touches: TouchAPI;
  
  // Parameter helper functions (return parameter objects)
  slider: (defaultValue: number, config: SliderConfig) => SliderParameter;
  color: (defaultValue: string, config: ColorConfig) => ColorParameter;
  toggle: (defaultValue: boolean, config: ToggleConfig) => ToggleParameter;
  select: (defaultValue: string | number, config: SelectConfig) => SelectParameter;
  text: (defaultValue: string, config: TextConfig) => TextParameter;
  number: (defaultValue: number, config: NumberConfig) => NumberParameter;
  image: (defaultValue: null, config: ImageConfig) => ImageParameter;
  
  // Utility methods
  useContext(type: '2d' | 'webgl'): OffscreenCanvasRenderingContext2D | WebGLRenderingContext | WebGL2RenderingContext | null;
}

// Audio API
export interface AudioAPI {
  isConnected: boolean;
  volume: {
    rms: number;
    peak: number;
  };
  beat?: {
    isKick: boolean;
    confidence: number;
  };
  bands: Record<string, number>;
  getFrequencyData: () => Uint8Array;
}

// Video API
export interface VideoAPI {
  isConnected: boolean;
  currentFrame: OffscreenCanvas | null;
  frameWidth: number;
  frameHeight: number;
  frameRate: number;
  getFrameData: () => ImageData | null;
    
  // CV Results (automatically cleaned up when features disabled)
  faces: FaceData[];
  hands: HandData[];
  pose: PoseData | null;
  segmentation: SegmentationData | null;
  
  // CV Control Interface (activation only)
  cv: {
    enableFaceDetection(enabled: boolean): Promise<void>;
    enableFaceMesh(enabled: boolean): Promise<void>;
    enableHandTracking(enabled: boolean): Promise<void>;
    enablePoseDetection(enabled: boolean): Promise<void>;
    enableBodySegmentation(enabled: boolean): Promise<void>;
    getActiveFeatures(): CVFeature[];
    isProcessing(): boolean;
  };
}

// CV Feature Types
export type CVFeature = 'faceDetection' | 'faceMesh' | 'handTracking' | 'poseDetection' | 'bodySegmentation';

// Enhanced Face Data with comprehensive landmarks and expressions
export interface FaceData {
  id: number;
  bounds: { x: number; y: number; width: number; height: number };
  confidence: number;
  landmarks?: { x: number; y: number; z?: number }[]; // 468 points for mesh, 6 points for basic
  expressions: {
    neutral: number;
    happy: number;
    sad: number;
    angry: number;
    surprised: number;
    disgusted: number;
    fearful: number;
  };
  headPose: {
    pitch: number; // Up/down rotation (-90 to 90 degrees)
    yaw: number;   // Left/right rotation (-90 to 90 degrees)
    roll: number;  // Tilt rotation (-180 to 180 degrees)
  };
}

// Enhanced Hand Data with comprehensive landmarks and gesture recognition
export interface HandData {
  id: number;
  handedness: 'left' | 'right';
  confidence: number;
  bounds: { x: number; y: number; width: number; height: number };
  landmarks: { x: number; y: number; z: number }[]; // 21 points
  
  // Palm and finger positions
  palm: { x: number; y: number; z: number };
  fingers: {
    thumb: { tip: {x: number; y: number; z: number}; extended: boolean };
    index: { tip: {x: number; y: number; z: number}; extended: boolean };
    middle: { tip: {x: number; y: number; z: number}; extended: boolean };
    ring: { tip: {x: number; y: number; z: number}; extended: boolean };
    pinky: { tip: {x: number; y: number; z: number}; extended: boolean };
  };
  
  // Recognized gestures with confidence scores
  gestures: {
    fist: number;         // 0-1 confidence
    openPalm: number;     // 0-1 confidence
    peace: number;        // 0-1 confidence
    thumbsUp: number;     // 0-1 confidence
    pointing: number;     // 0-1 confidence
  };
}

// Body Pose Data with 33-point BlazePose landmarks
export interface PoseData {
  confidence: number;
  landmarks: { x: number; y: number; z: number; visibility: number }[]; // 33 points
  
  // Key body parts for easy access
  face: { x: number; y: number }[];
  torso: { x: number; y: number }[];
  leftArm: { x: number; y: number }[];
  rightArm: { x: number; y: number }[];
  leftLeg: { x: number; y: number }[];
  rightLeg: { x: number; y: number }[];
}

// Body Segmentation Data for background replacement effects
export interface SegmentationData {
  mask: Uint8Array; // Per-pixel segmentation mask (0=background, 1=person)
  width: number;    // Mask width in pixels
  height: number;   // Mask height in pixels
}

// CV Processing Statistics
export interface CVProcessingStats {
  activeFeatures: CVFeature[];
  processingTime: number; // average processing time in milliseconds
  effectiveFPS: number; // target CV FPS based on scene rate and CV rate mode
  actualFPS: number; // actual measured CV processing FPS
  isProcessing: boolean;
}

export interface CVWorkerInitMessage {
  type: 'init';
}

export interface CVWorkerConfigMessage {
  type: 'config';
  features: CVFeature[];
}

export interface CVWorkerProcessMessage {
  type: 'process';
  bitmap: ImageBitmap;
  timestamp: number;
  features: CVFeature[];
}

export interface CVWorkerResultPayload {
  faces?: FaceData[];
  hands?: HandData[];
  pose?: PoseData | null;
  segmentation?: SegmentationData | null;
  timestamp: number;
}

export interface CVWorkerResultMessage {
  type: 'result';
  success: boolean;
  data?: CVWorkerResultPayload;
  error?: string;
}

// Input APIs - Phase 7 Implementation
export interface MouseAPI {
  // Position (canvas coordinates)
  x: number;
  y: number;
  
  // Canvas bounds
  isInCanvas: boolean;
  
  // Button states
  isPressed: boolean;
  leftButton: boolean;
  rightButton: boolean;
  middleButton: boolean;
  
  // Movement
  velocity: { x: number; y: number };
  deltaX: number;
  deltaY: number;
  
  // Scroll wheel
  wheelDelta: number;
  wheelX: number;
  wheelY: number;
  
  // Events (frame-based)
  wasPressed: boolean;
  wasReleased: boolean;
  wasMoved: boolean;
}

export interface KeyboardAPI {
  // Key state queries
  isPressed(key: string): boolean;
  wasPressed(key: string): boolean;
  wasReleased(key: string): boolean;
  
  // Active state
  activeKeys: Set<string>;
  pressedThisFrame: Set<string>;
  releasedThisFrame: Set<string>;
  
  // Recent activity
  lastKeyPressed: string;
  lastKeyReleased: string;
  
  // Modifier keys
  shift: boolean;
  ctrl: boolean;
  alt: boolean;
  meta: boolean;
}

export interface TouchPoint {
  id: number;
  x: number;
  y: number;
  pressure: number;
  radius: number;
  radiusX: number;
  radiusY: number;
  rotationAngle: number;
  force: number;
  
  // Movement
  deltaX: number;
  deltaY: number;
  velocity: { x: number; y: number };
  
  // Lifecycle
  isNew: boolean;
  isActive: boolean;
  isEnding: boolean;
}

export interface TouchAPI {
  // Current touches
  points: TouchPoint[];
  count: number;
  
  // Touch events
  started: TouchPoint[];
  moved: TouchPoint[];
  ended: TouchPoint[];
  
  // Convenience accessors
  primary: TouchPoint | null;
  
  // Gesture detection
  gestures: TouchGestureAPI;
}

export interface TouchGestureAPI {
  // Gesture states
  isPinching: boolean;
  isRotating: boolean;
  isPanning: boolean;
  isTapping: boolean;
  
  // Gesture values
  pinchScale: number;
  pinchDelta: number;
  rotationAngle: number;
  rotationDelta: number;
  panDelta: { x: number; y: number };
  
  // Tap detection
  tapCount: number;
  lastTapTime: number;
  tapPosition: { x: number; y: number } | null;
}

// Enhanced Parameter API for Phase 2
export interface ParameterAPI {
  // Define parameters (called once outside render loop)
  define(groups: ParameterGroup[]): void;
  
  // Proxy access to parameter values (fast render-time access)
  [key: string]: any;
}

// Error types
export class VijiCoreError extends Error {
  constructor(
    message: string,
    public code: string,
    public context?: any
  ) {
    super(message);
    this.name = 'VijiCoreError';
  }
} 